import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const Login = ({ setUser }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    
    // Forgot password states
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotError, setForgotError] = useState('');
    const [forgotMessage, setForgotMessage] = useState('');
    const [forgotUserId, setForgotUserId] = useState(null);
    const [forgotCode, setForgotCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [forgotStage, setForgotStage] = useState('email'); // 'email', 'code', 'password'
    const [forgotLoading, setForgotLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const res = await axios.post('/api/login', { 
                email: email.trim(),
                password: password.trim()
            });
            
            localStorage.setItem('user', JSON.stringify(res.data));
            setUser(res.data);
            navigate('/');
        } catch (err) {
            setError("Invalid email or password. Please try again.");
        }
    };
    
    const handleForgotPasswordRequest = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        setForgotError('');
        setForgotMessage('');
        
        try {
            const res = await axios.post('/api/forgot-password/request', {
                email: forgotEmail.trim()
            });
            setForgotUserId(res.data.userId);
            setForgotStage('code');
            setForgotMessage('A reset code has been sent to your email. Please enter it below.');
            setForgotLoading(false);
        } catch (err) {
            setForgotError(err.response?.data || "Error requesting password reset");
            setForgotLoading(false);
        }
    };
    
    const handleVerifyForgotCode = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        setForgotError('');
        setForgotMessage('');
        
        if (!forgotCode) {
            setForgotError('Please enter the verification code');
            setForgotLoading(false);
            return;
        }
        
        setForgotStage('password');
        setForgotMessage('');
        setForgotLoading(false);
    };
    
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        setForgotError('');
        setForgotMessage('');
        
        if (newPassword !== confirmNewPassword) {
            setForgotError('Passwords do not match');
            setForgotLoading(false);
            return;
        }
        
        if (newPassword.length < 6) {
            setForgotError('Password must be at least 6 characters');
            setForgotLoading(false);
            return;
        }
        
        try {
            await axios.post('/api/forgot-password/reset', {
                userId: forgotUserId,
                verificationCode: forgotCode,
                newPassword: newPassword
            });
            setForgotMessage('Password reset successfully! Please log in with your new password.');
            setTimeout(() => {
                setShowForgotPassword(false);
                setForgotStage('email');
                setForgotEmail('');
                setForgotCode('');
                setNewPassword('');
                setConfirmNewPassword('');
                setForgotUserId(null);
                setForgotMessage('');
            }, 2000);
            setForgotLoading(false);
        } catch (err) {
            setForgotError(err.response?.data || "Error resetting password");
            setForgotLoading(false);
        }
    };

    return (
        <div className="page centered">
            {!showForgotPassword ? (
                <form onSubmit={handleSubmit} className="card auth-card stack">
                    <div className="stack" style={{ gap: '6px', textAlign: 'center' }}>
                        <h2 className="title">Archive Login</h2>
                        <p className="subtitle">Please enter your staff credentials</p>
                    </div>
                    
                    {error && <div className="notice notice-danger">{error}</div>}

                    <div className="stack" style={{ gap: '6px' }}>
                        <label className="label">Email Address</label>
                        <input 
                            type="email" 
                            placeholder="admin@gmail.com" 
                            value={email}
                            onChange={(e) => setEmail(e.target.value)} 
                            className="input" 
                            required 
                        />
                    </div>

                    <div className="stack" style={{ gap: '6px' }}>
                        <label className="label">Password</label>
                        <div style={{ position: 'relative' }}>
                            <input 
                                type={showPassword ? "text" : "password"} 
                                placeholder="••••••••" 
                                value={password}
                                onChange={(e) => setPassword(e.target.value)} 
                                className="input"
                                style={{ paddingRight: '60px' }}
                                required 
                            />
                            <button 
                                type="button"
                                onMouseDown={() => setShowPassword(true)}
                                onMouseUp={() => setShowPassword(false)}
                                onMouseLeave={() => setShowPassword(false)}
                                className="btn btn-ghost"
                                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px' }}
                            >
                                {showPassword ? "VIEW" : "SHOW"}
                            </button>
                        </div>
                    </div>

                    <button type="submit" className="btn btn-primary">Sign In</button>
                    
                    <div style={{ textAlign: 'center' }}>
                        <button 
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="btn btn-ghost"
                        >
                            Forgot Password?
                        </button>
                    </div>
                </form>
            ) : (
                <div className="card auth-card stack">
                    {forgotStage === 'email' && (
                        <form onSubmit={handleForgotPasswordRequest}>
                            <h2 className="title" style={{ textAlign: 'center' }}>Reset Password</h2>
                            <p className="subtitle" style={{ textAlign: 'center', marginBottom: '20px' }}>Enter your email address to receive a reset code</p>
                            
                            {forgotError && <div className="notice notice-danger">{forgotError}</div>}
                            {forgotMessage && <div className="notice notice-success">{forgotMessage}</div>}

                            <div className="stack" style={{ gap: '6px', marginBottom: '16px' }}>
                                <label className="label">Email Address</label>
                                <input 
                                    type="email" 
                                    placeholder="your@email.com" 
                                    value={forgotEmail}
                                    onChange={(e) => setForgotEmail(e.target.value)} 
                                    className="input" 
                                    required 
                                />
                            </div>

                            <div className="row">
                                <button type="submit" className="btn btn-primary" disabled={forgotLoading}>
                                    {forgotLoading ? 'Sending...' : 'Send Reset Code'}
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => setShowForgotPassword(false)}
                                    className="btn btn-outline"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                    
                    {forgotStage === 'code' && (
                        <form onSubmit={handleVerifyForgotCode}>
                            <h2 className="title" style={{ textAlign: 'center' }}>Enter Reset Code</h2>
                            
                            {forgotError && <div className="notice notice-danger">{forgotError}</div>}
                            {forgotMessage && <div className="notice notice-success">{forgotMessage}</div>}

                            <div className="stack" style={{ gap: '6px', marginBottom: '16px' }}>
                                <label className="label">Verification Code</label>
                                <input 
                                    type="text" 
                                    placeholder="Enter 6-digit code"
                                    maxLength="6"
                                    value={forgotCode}
                                    onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ''))} 
                                    className="input" 
                                    required 
                                />
                            </div>

                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={forgotLoading || forgotCode.length !== 6}
                            >
                                {forgotLoading ? 'Verifying...' : 'Verify Code'}
                            </button>
                        </form>
                    )}
                    
                    {forgotStage === 'password' && (
                        <form onSubmit={handleResetPassword}>
                            <h2 className="title" style={{ textAlign: 'center' }}>Set New Password</h2>
                            
                            {forgotError && <div className="notice notice-danger">{forgotError}</div>}
                            {forgotMessage && <div className="notice notice-success">{forgotMessage}</div>}

                            <div className="stack" style={{ gap: '6px', marginBottom: '16px' }}>
                                <label className="label">New Password</label>
                                <input 
                                    type="password" 
                                    placeholder="••••••••" 
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)} 
                                    className="input" 
                                    required 
                                />
                            </div>

                            <div className="stack" style={{ gap: '6px', marginBottom: '16px' }}>
                                <label className="label">Confirm Password</label>
                                <input 
                                    type="password" 
                                    placeholder="••••••••" 
                                    value={confirmNewPassword}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)} 
                                    className="input" 
                                    required 
                                />
                            </div>

                            <button type="submit" className="btn btn-primary" disabled={forgotLoading}>
                                {forgotLoading ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
};

export default Login;
